import React, { useState, useEffect } from 'react';
import scheduleData from './scheduleData.json';

function parseTime(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
}

function getCurrentLecture(lectures) {
    const now = new Date();
    const currentDay = ['Неделя', 'Понеделник', 'Вторник', 'Сряда', 'Четвъртък', 'Петък', 'Събота'][now.getDay()];
    const currentTime = now.getHours() * 60 + now.getMinutes();

    return lectures.find(lecture => {
        if (lecture.day !== currentDay) return false;
        const [start, end] = lecture.hour.split('-').map(parseTime);
        return currentTime >= start && currentTime < end;
    });
}

function getNextLecture(lectures, currentLecture) {
    const currentIndex = lectures.indexOf(currentLecture);
    const sameDayLectures = lectures.filter(lecture => lecture.day === currentLecture.day);
    const nextSameDayLecture = sameDayLectures.find((lecture, index) =>
        index > sameDayLectures.indexOf(currentLecture)
    );
    return nextSameDayLecture || null;
}

export default function CurrentLecture({ isDarkMode }) {
    const [currentLecture, setCurrentLecture] = useState(null);
    const [nextLecture, setNextLecture] = useState(null);
    const [progress, setProgress] = useState(0);
    const [timeLeft, setTimeLeft] = useState('');
    const [status, setStatus] = useState('default');
    const [currentDay, setCurrentDay] = useState('');

    useEffect(() => {
        const updateLecture = () => {
            const now = new Date();
            const lectures = scheduleData.lectures;
            const lecture = getCurrentLecture(lectures);
            const currentDayName = ['Неделя', 'Понеделник', 'Вторник', 'Сряда', 'Четвъртък', 'Петък', 'Събота'][now.getDay()];
            setCurrentDay(currentDayName);

            setCurrentLecture(lecture);

            if (lecture) {
                const nextLec = getNextLecture(lectures, lecture);
                setNextLecture(nextLec);

                const [start, end] = lecture.hour.split('-').map(parseTime);
                const currentTime = now.getHours() * 60 + now.getMinutes();
                const totalDuration = end - start;
                const elapsed = currentTime - start;
                const newProgress = Math.min(100, (elapsed / totalDuration) * 100);
                setProgress(newProgress);

                const minutesLeft = end - currentTime;
                const secondsLeft = 60 - (now.getSeconds() % 60);

                if (minutesLeft > 1) {
                    setTimeLeft(`${minutesLeft} мин`);
                } else if (minutesLeft === 1) {
                    setTimeLeft(`${secondsLeft} сек`);
                } else {
                    setTimeLeft(`${secondsLeft} сек`);
                }

                if (minutesLeft <= 10) {
                    setStatus('orange');
                } else {
                    setStatus('green');
                }
            } else {
                const currentTime = now.getHours() * 60 + now.getMinutes();
                const currentDay = ['Неделя', 'Понеделник', 'Вторник', 'Сряда', 'Четвъртък', 'Петък', 'Събота'][now.getDay()];

                const nextLecture = lectures.find(l => {
                    const [start] = l.hour.split('-').map(parseTime);
                    return l.day === currentDay && start > currentTime;
                });

                if (nextLecture) {
                    setStatus('orange');
                    setNextLecture(nextLecture);
                } else {
                    setStatus('default');
                    setNextLecture(null);
                }
                setProgress(0);
                setTimeLeft('');
            }
        };

        updateLecture();
        const interval = setInterval(updateLecture, 1000); // Update every second

        return () => clearInterval(interval);
    }, []);

    if (!currentLecture && !nextLecture) {
        return (
            <div className="current-lecture-container">
                <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
                    <div className={`day-box ${isDarkMode ? 'dark-mode' : ''}`}>
                        <h3 className="gradient-text">{currentDay}</h3>
                    </div>
                </div>
                <div className="current-lecture-card no-lecture">
                    <div className="no-lecture-icon">📚</div>
                    <h3>Няма планирани лекции за днес</h3>
                    <p>Свободен ден или почивка</p>
                </div>
            </div>
        );
    }

    return (
        <div className="current-lecture-container">
            <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
                <div className={`day-box ${isDarkMode ? 'dark-mode' : ''}`}>
                    <h3 className="gradient-text">{currentDay}</h3>
                </div>
            </div>
            
            {currentLecture && (
                <div className={`current-lecture-card active type-${currentLecture.type.toLowerCase().replace(' ', '-')}`}>
                    <div className="lecture-type-header">
                        <span className={`lecture-type-badge ${currentLecture.type.toLowerCase().replace(' ', '-')}`}>
                            {currentLecture.type}
                        </span>
                        <span className="live-indicator">
                            <span className="live-dot"></span>
                            В момента
                        </span>
                    </div>
                    
                    <div className="lecture-content">
                        <h2 className="lecture-title">{currentLecture.lecture}</h2>
                        
                        <div className="lecture-info">
                            <div className="info-item">
                                <span className="info-icon">👨‍🏫</span>
                                <span>{currentLecture.lecturer}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-icon">🚪</span>
                                <span>Стая {currentLecture.roomNo}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="progress-section">
                        <div className="progress-info">
                            <span className="time-remaining">⏱️ {timeLeft} остават</span>
                            <span className="progress-percent">{Math.round(progress)}%</span>
                        </div>
                        <div className="progress-bar-container">
                            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>
                    
                    <div className="lecture-time">
                        <span className="time-icon">🕐</span>
                        <span className="time-text">{currentLecture.hour}</span>
                    </div>
                </div>
            )}
            
            {(status === 'orange' || !currentLecture) && nextLecture && (
                <div className={`next-lecture-card type-${nextLecture.type.toLowerCase().replace(' ', '-')}`}>
                    <div className="next-lecture-header">
                        <h3>📌 Следваща лекция</h3>
                    </div>
                    
                    <div className="lecture-type-header">
                        <span className={`lecture-type-badge ${nextLecture.type.toLowerCase().replace(' ', '-')}`}>
                            {nextLecture.type}
                        </span>
                    </div>
                    
                    <div className="lecture-content">
                        <h2 className="lecture-title">{nextLecture.lecture}</h2>
                        
                        <div className="lecture-info">
                            <div className="info-item">
                                <span className="info-icon">👨‍🏫</span>
                                <span>{nextLecture.lecturer}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-icon">🚪</span>
                                <span>Стая {nextLecture.roomNo}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="lecture-time">
                        <span className="time-icon">🕐</span>
                        <span className="time-text">{nextLecture.hour}</span>
                    </div>
                </div>
            )}
        </div>
    );
}